export type ProductGatingFieldKey =
  | "firstName"
  | "lastName"
  | "email"
  | "phoneNumber"
  | "instagramUsername";

export interface ProductGatingField {
  field: ProductGatingFieldKey;
  label: string;
  required: boolean;
  enabled: boolean;
}

export interface ProductGating {
  enabled: boolean;
  fields: ProductGatingField[];
}

export const DEFAULT_PRODUCT_GATING_FIELDS: ProductGatingField[] = [
  {
    field: "firstName",
    label: "First Name",
    required: true,
    enabled: true,
  },
  {
    field: "lastName",
    label: "Last Name",
    required: false,
    enabled: true,
  },
  {
    field: "email",
    label: "Email Address",
    required: true,
    enabled: true,
  },
  {
    field: "phoneNumber",
    label: "Phone Number",
    required: false,
    enabled: false,
  },
  {
    field: "instagramUsername",
    label: "Instagram Username",
    required: false,
    enabled: false,
  },
];

const GATING_FIELD_KEYS = new Set<string>(
  DEFAULT_PRODUCT_GATING_FIELDS.map((field) => field.field),
);

export function resolveProductGatingFields(
  fields: ProductGatingField[] | undefined,
): ProductGatingField[] {
  if (!fields?.length) {
    return DEFAULT_PRODUCT_GATING_FIELDS.map((field) => ({ ...field }));
  }

  const byKey = new Map(fields.map((field) => [field.field, field]));
  return DEFAULT_PRODUCT_GATING_FIELDS.map((defaultField) => {
    const existing = byKey.get(defaultField.field);
    if (!existing) {
      return { ...defaultField };
    }
    return {
      field: defaultField.field,
      label:
        typeof existing.label === "string" && existing.label.trim()
          ? existing.label.trim()
          : defaultField.label,
      required: defaultField.required,
      enabled: existing.enabled === true,
    };
  });
}

export function resolveProductGating(
  gating: ProductGating | undefined,
): ProductGating {
  return {
    enabled: gating?.enabled === true,
    fields: resolveProductGatingFields(gating?.fields),
  };
}

export function isProductGatingFieldKey(
  value: unknown,
): value is ProductGatingFieldKey {
  return typeof value === "string" && GATING_FIELD_KEYS.has(value);
}
